/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaMinusCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

import ProductNameSearch from '../../../../components/ProductNameSearch';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { CtaButton, IconButton } from '../../../../styles/ui/Button';
import { QtyInput } from '../../../../styles/ui/Form';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { DataTable } from '../../../../styles/ui/Table';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { FlagRow } from './modalKit';

// =============================================================================
// Editor da lista de itens do PEDIDO — compartilhado pela criação e pela
// edição da ordem. Em ambos os casos a lista enviada é o ESTADO COMPLETO
// desejado (item ausente = remoção), então o editor é a fonte da verdade da
// tela inteira, não um "adicionador".
//
// Dois modos (fase 2 da refatoração visual, 2026-09-10):
//
// · CRIAÇÃO (default): colunas SKU · Produto · [Disponível] · Quantidade ·
//   [Sobra] · Ações. Disponibilidade é informativa (D3), nunca gate.
//
// · EDIÇÃO (`originalByProdId` fornecido): entra a coluna "Expedido", as tags
//   "Novo" (item incluído) / "Expedido" (item travado, linha cinza), a nota
//   por linha ("Original: N un" · "Mín. N (expedido)") e o PISO DO EXPEDIDO
//   por VALIDAÇÃO: digitar abaixo é permitido, mas a linha marca em vermelho
//   com "Mínimo N un — já expedido." e a tela bloqueia o salvar (H.3.1 — o
//   servidor recusaria com 422 `transfer_requested_below_shipped`). Remoção de
//   item com expedição continua TRAVADA (lixeira desabilitada com motivo).
//   A coluna "Sobra" fica fora do modo edição (protótipo fase 2).
// =============================================================================

const NarrowQtyInput = styled(QtyInput)`
   max-width: 110px;
`;

const NoteText = styled.small`
   display: block;
   margin-top: 4px;
   font-size: 11px;
   font-weight: ${({ $error }) => ($error ? 700 : 600)};
   color: ${({ $error }) => ($error ? '#c62828' : 'rgba(0, 0, 0, 0.5)')};
`;

const TagChip = styled.span`
   display: inline-flex;
   align-items: center;
   margin-left: 8px;
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   white-space: nowrap;
   vertical-align: middle;
   background: ${({ $kind }) =>
      $kind === 'added' ? 'rgba(64,152,239,0.14)' : 'rgba(46,125,50,0.12)'};
   color: ${({ $kind }) => ($kind === 'added' ? '#0b3a63' : '#1b5e20')};
`;

const SobraCell = styled.td`
   && {
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: ${({ $negative }) => ($negative ? '#c62828' : 'inherit')};
   }
`;

const integerRegex = /^[0-9]+$/;

export default function TransferItemsEditor({
   items,
   onChange,
   shippedByProdId = {},
   availabilityByProdId = null,
   originalByProdId = null,
   disabled = false,
   emptyDescription = 'Pesquise um produto, informe a quantidade e adicione à lista.',
}) {
   const [productQuery, setProductQuery] = useState('');
   const [selectedProduct, setSelectedProduct] = useState(null);
   const [quantity, setQuantity] = useState('');

   const quantityRef = useRef(null);

   const shippedOf = (prodId) => Number(shippedByProdId[prodId] ?? 0);

   const showAvailability = availabilityByProdId != null;
   // Produto fora da resposta da posição = todos os saldos zerados.
   const availableOf = (prodId) => Number(availabilityByProdId?.[prodId] ?? 0);

   const isEditMode = originalByProdId != null;
   const showSobra = showAvailability && !isEditMode;

   const handleAdd = () => {
      if (!selectedProduct) {
         toast.error('Selecione um produto na busca.');
         return;
      }
      const qty = Number.parseInt(quantity, 10);
      if (!Number.isFinite(qty) || qty <= 0) {
         toast.error('Informe uma quantidade maior que zero.');
         return;
      }

      const prodId = Number(selectedProduct.id);
      const existingIndex = items.findIndex(
         (item) => Number(item.prod_id) === prodId
      );

      // Produto repetido soma quantidade (mesma UX das telas de movimento) —
      // o contrato exige produto único por linha.
      if (existingIndex >= 0) {
         const next = [...items];
         next[existingIndex] = {
            ...next[existingIndex],
            requested_qtd: Number(next[existingIndex].requested_qtd) + qty,
         };
         onChange(next);
      } else {
         onChange([
            ...items,
            {
               prod_id: prodId,
               prod_sku: selectedProduct.sku ?? selectedProduct.prod_sku ?? null,
               prod_name:
                  selectedProduct.name ?? selectedProduct.prod_name ?? null,
               requested_qtd: qty,
            },
         ]);
      }

      setProductQuery('');
      setSelectedProduct(null);
      setQuantity('');
   };

   const handleQtyChange = (prodId, rawValue) => {
      if (rawValue !== '' && !integerRegex.test(rawValue)) return;
      const next = items.map((item) =>
         Number(item.prod_id) === Number(prodId)
            ? { ...item, requested_qtd: rawValue }
            : item
      );
      onChange(next);
   };

   const handleRemove = (prodId) => {
      if (shippedOf(prodId) > 0) return;
      onChange(items.filter((item) => Number(item.prod_id) !== Number(prodId)));
   };

   const handleQtyKeyDown = (event) => {
      if (event.key === 'Enter') {
         event.preventDefault();
         handleAdd();
      }
   };

   // Estado derivado por linha — só no modo edição existem erro, tag e nota.
   const rowState = (item) => {
      const shipped = shippedOf(item.prod_id);
      const isLocked = shipped > 0;
      if (!isEditMode) {
         return { shipped, isLocked, error: null, note: null, tag: null };
      }

      const qty = Number(item.requested_qtd || 0);
      const qtyEmpty =
         String(item.requested_qtd ?? '').trim() === '' || qty < 1;

      let error = null;
      if (qtyEmpty) error = 'Informe ao menos 1 un.';
      else if (qty < shipped) error = `Mínimo ${shipped} un — já expedido.`;

      const original = originalByProdId[item.prod_id];
      const isAdded = original == null;
      const isChanged = !isAdded && !qtyEmpty && qty !== Number(original);

      let note = error;
      if (!note && isChanged) note = `Original: ${original} un`;
      if (!note && isLocked) note = `Mín. ${shipped} (expedido)`;

      let tag = null;
      if (isAdded) tag = 'added';
      else if (isLocked) tag = 'locked';

      return { shipped, isLocked, error, note, tag };
   };

   return (
      <>
         <FormGrid $cols={12} $colsMobile={12}>
            <Field $col={8} $colMobile={12}>
               <label htmlFor="transfer-item-product">
                  {isEditMode ? 'Adicionar produto' : 'Produto'}
               </label>
               <ProductNameSearch
                  value={productQuery}
                  onChange={setProductQuery}
                  onSelect={(product) => {
                     setSelectedProduct(product || null);
                     if (product) quantityRef.current?.focus();
                  }}
                  nameLike
                  displayMode="sku-name"
                  placeholder="Digite o SKU ou o nome e selecione o produto..."
                  disabled={disabled}
               />
            </Field>

            <Field $col={2} $colMobile={6}>
               <label htmlFor="transfer-item-qty">Quantidade</label>
               <input
                  id="transfer-item-qty"
                  type="text"
                  inputMode="numeric"
                  ref={quantityRef}
                  value={quantity}
                  disabled={disabled}
                  placeholder="0"
                  onChange={(event) => {
                     const { value } = event.target;
                     if (value === '' || integerRegex.test(value)) {
                        setQuantity(value);
                     }
                  }}
                  onKeyDown={handleQtyKeyDown}
               />
            </Field>

            <Field $col={2} $colMobile={6} $alignEnd>
               <label style={{ visibility: 'hidden' }}>Adicionar</label>
               <CtaButton
                  type="button"
                  onClick={handleAdd}
                  disabled={disabled || !selectedProduct || !quantity}
                  style={{ width: '100%' }}
               >
                  Adicionar
               </CtaButton>
            </Field>
         </FormGrid>

         {items.length === 0 ? (
            <EmptyState
               title="Nenhum item na transferência"
               description={emptyDescription}
            />
         ) : (
            // $minWidth="0": sem o piso default de 720px — com os headers em
            // duas linhas a tabela encolhe junto com o composer quando divide
            // a largura com o aside.
            <TableWrapper style={{ marginTop: 16 }} $minWidth="0">
               <DataTable>
                  <thead>
                     <tr>
                        <th className="center">SKU</th>
                        <th>Produto</th>
                        {showAvailability && (
                           <th
                              className="num"
                              title="Disponível = físico − reservado no PDV de origem"
                           >
                              Disponível
                              <br />
                              na origem
                           </th>
                        )}
                        {isEditMode && (
                           <th
                              className="num"
                              title="Já saiu da origem — é o piso da quantidade"
                           >
                              Expedido
                           </th>
                        )}
                        <th className="center">
                           Quantidade
                           <br />
                           solicitada
                        </th>
                        {showSobra && (
                           <th
                              className="num"
                              title="Disponível − pedido. Negativa é permitida: a reserva pode passar do disponível."
                           >
                              Sobra
                           </th>
                        )}
                        <th className="center">Ações</th>
                     </tr>
                  </thead>
                  <tbody>
                     {items.map((item) => {
                        const state = rowState(item);
                        const available = availableOf(item.prod_id);
                        const sobra =
                           available - Number(item.requested_qtd || 0);
                        const flag =
                           (state.error && 'invalid') ||
                           (state.isLocked && 'locked') ||
                           null;
                        return (
                           <FlagRow key={item.prod_id} $flag={flag}>
                              <td className="center">{item.prod_sku ?? '—'}</td>
                              <td>
                                 {item.prod_name ?? `Produto ${item.prod_id}`}
                                 {state.tag === 'added' && (
                                    <TagChip $kind="added">Novo</TagChip>
                                 )}
                                 {state.tag === 'locked' && (
                                    <TagChip
                                       $kind="locked"
                                       title={`${state.shipped} un já expedidas — quantidade travada no piso, item sem remoção`}
                                    >
                                       Expedido
                                    </TagChip>
                                 )}
                              </td>
                              {showAvailability && (
                                 <td className="num">{available}</td>
                              )}
                              {isEditMode && (
                                 <td className="num">
                                    {state.shipped > 0 ? state.shipped : '—'}
                                 </td>
                              )}
                              <td className="center">
                                 <NarrowQtyInput
                                    type="text"
                                    inputMode="numeric"
                                    aria-label={`Quantidade solicitada de ${
                                       item.prod_name ?? item.prod_id
                                    }`}
                                    aria-invalid={!!state.error || undefined}
                                    $invalid={!!state.error}
                                    value={item.requested_qtd}
                                    disabled={disabled}
                                    onChange={(event) =>
                                       handleQtyChange(
                                          item.prod_id,
                                          event.target.value
                                       )
                                    }
                                 />
                                 {state.note && (
                                    <NoteText $error={!!state.error}>
                                       {state.note}
                                    </NoteText>
                                 )}
                              </td>
                              {showSobra && (
                                 <SobraCell
                                    className="num"
                                    $negative={sobra < 0}
                                 >
                                    {sobra}
                                 </SobraCell>
                              )}
                              <td className="center">
                                 <IconButton
                                    type="button"
                                    $intent="danger"
                                    disabled={disabled || state.isLocked}
                                    onClick={() => handleRemove(item.prod_id)}
                                    title={
                                       state.isLocked
                                          ? `Item com ${state.shipped} un já expedidas — não pode ser removido do pedido`
                                          : 'Remover item'
                                    }
                                 >
                                    <FaMinusCircle size={18} />
                                 </IconButton>
                              </td>
                           </FlagRow>
                        );
                     })}
                  </tbody>
               </DataTable>
            </TableWrapper>
         )}
      </>
   );
}

TransferItemsEditor.propTypes = {
   items: PropTypes.arrayOf(
      PropTypes.shape({
         prod_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         prod_sku: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         prod_name: PropTypes.string,
         requested_qtd: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
         ]),
      })
   ).isRequired,
   onChange: PropTypes.func.isRequired,
   // { [prod_id]: shipped_qtd } — pisos vindos da ordem em edição.
   // eslint-disable-next-line react/forbid-prop-types
   shippedByProdId: PropTypes.object,
   // { [prod_id]: available_qtd } da ORIGEM — liga as colunas de
   // disponibilidade (informativas). null/undefined = colunas ocultas.
   // eslint-disable-next-line react/forbid-prop-types
   availabilityByProdId: PropTypes.object,
   // { [prod_id]: requested_qtd ORIGINAL } — liga o MODO EDIÇÃO (coluna
   // Expedido, tags, notas e piso por validação). null/undefined = criação.
   // eslint-disable-next-line react/forbid-prop-types
   originalByProdId: PropTypes.object,
   disabled: PropTypes.bool,
   emptyDescription: PropTypes.string,
};
